import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

export interface LessonLaunchOptions {
  /** SPA destination to navigate to once the overlay has covered the screen. */
  readonly to: string;
  /** Lesson background (gradient or solid) that matches the destination's themed background. */
  readonly background: string;
  /** Color for the large icon watermark. */
  readonly watermarkColor: string;
  /** Lesson icon (null falls back to a plain color wash). */
  readonly iconDef: IconDefinition | null;
}

interface LessonLaunchContextValue {
  readonly launch: (opts: LessonLaunchOptions) => void;
}

const LessonLaunchContext = createContext<LessonLaunchContextValue | null>(null);

/** Marker placed on navigation state so the destination skips its "wash" intro. */
export const LAUNCH_STATE_FLAG = "ccsLaunch";

const FADE_IN_MS = 340;
const FADE_OUT_MS = 460;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

interface OverlayState {
  readonly background: string;
  readonly watermarkColor: string;
  readonly iconDef: IconDefinition | null;
  readonly phase: "in" | "out";
}

export function LessonLaunchProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [overlay, setOverlay] = useState<OverlayState | null>(null);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const handle = timers;
    return () => {
      handle.current.forEach((t) => window.clearTimeout(t));
      handle.current = [];
    };
  }, []);

  const launch = useCallback(
    (opts: LessonLaunchOptions) => {
      const navState = { [LAUNCH_STATE_FLAG]: true };
      if (prefersReducedMotion()) {
        navigate(opts.to, { state: navState });
        return;
      }
      timers.current.forEach((t) => window.clearTimeout(t));
      timers.current = [];
      setOverlay({
        background: opts.background,
        watermarkColor: opts.watermarkColor,
        iconDef: opts.iconDef,
        phase: "in",
      });
      timers.current.push(
        window.setTimeout(() => {
          navigate(opts.to, { state: navState });
          setOverlay((prev) => (prev ? { ...prev, phase: "out" } : prev));
          timers.current.push(window.setTimeout(() => setOverlay(null), FADE_OUT_MS));
        }, FADE_IN_MS)
      );
    },
    [navigate]
  );

  return (
    <LessonLaunchContext.Provider value={{ launch }}>
      {children}
      {overlay ? (
        <div
          className="lesson-launch-overlay"
          data-phase={overlay.phase}
          style={{ background: overlay.background }}
          aria-hidden="true"
        >
          {overlay.iconDef ? (
            <FontAwesomeIcon
              icon={overlay.iconDef}
              className="lesson-launch-overlay-icon"
              style={{ color: overlay.watermarkColor }}
            />
          ) : null}
        </div>
      ) : null}
    </LessonLaunchContext.Provider>
  );
}

/** Returns a `launch` fn that plays the cross-fade overlay, then navigates. */
export function useLessonLaunch(): (opts: LessonLaunchOptions) => void {
  const ctx = useContext(LessonLaunchContext);
  const navigate = useNavigate();
  return useCallback(
    (opts: LessonLaunchOptions) => {
      if (ctx) {
        ctx.launch(opts);
        return;
      }
      navigate(opts.to, { state: { [LAUNCH_STATE_FLAG]: true } });
    },
    [ctx, navigate]
  );
}
